import React, { useState, useEffect, useRef } from "react";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import UserService from "../services/UserService";
import Loading from "../hooks/Loading";
import { useRegisterForm } from "../components/user/hooks/useRegisterForm";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import provincesCanada from "../utils/provincesCanada";
import citiesCanada from "../utils/citiesCanada";
import LoadingSpinner from '../utils/LoadingSpinner';

const AutocompleteInput = ({
                               name,
                               value,
                               options,
                               onChange,
                               placeholder,
                               label,
                               error,
                               touched,
                               disabled = false,
                               allowFreeInput = false // Nouvelle prop pour autoriser la saisie libre
                           }) => {
    const [suggestions, setSuggestions] = useState([]);
    const [inputValue, setInputValue] = useState(value);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const containerRef = useRef(null);
    const suggestionsRef = useRef(null);

    useEffect(() => {
        setInputValue(value);
    }, [value]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                containerRef.current &&
                !containerRef.current.contains(event.target) &&
                suggestionsRef.current &&
                !suggestionsRef.current.contains(event.target)
            ) {
                setShowSuggestions(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleInputChange = (e) => {
        if (disabled) return;
        const newValue = e.target.value;
        setInputValue(newValue);

        // Mettre à jour la valeur directement si on autorise la saisie libre
        if (allowFreeInput) {
            onChange({
                target: {
                    name,
                    value: newValue,
                },
            });
        }

        setShowSuggestions(true);

        // Filtrer les suggestions basées sur la saisie
        const filteredSuggestions = options.filter((option) =>
            option.toLowerCase().includes(newValue.toLowerCase())
        );
        setSuggestions(filteredSuggestions);
    };

    const handleSuggestionClick = (suggestion) => {
        if (disabled) return;

        setInputValue(suggestion);
        onChange({
            target: {
                name,
                value: suggestion,
            },
        });
        setShowSuggestions(false);
    };

    const handleBlur = () => {
        if (disabled) return;

        // Si on autorise la saisie libre, on accepte n'importe quelle valeur
        if (allowFreeInput) {
            onChange({
                target: {
                    name,
                    value: inputValue,
                },
            });
        } else {
            // Vérifier si la valeur saisie est dans les options
            const isValidOption = options.some(
                (option) => option.toLowerCase() === inputValue.toLowerCase()
            );

            if (!isValidOption) {
                onChange({
                    target: {
                        name,
                        value: "",
                    },
                });
            }
        }
    };

    const handleFocus = () => {
        if (disabled) return;

        if (inputValue) {
            const filteredSuggestions = options.filter((option) =>
                option.toLowerCase().includes(inputValue.toLowerCase())
            );
            setSuggestions(filteredSuggestions);
            setShowSuggestions(true);
        }
    };

    return (
        <div
            className="form-floating mt-24 position-relative"
            ref={containerRef}
            style={{ zIndex: showSuggestions ? 1000 : 1 }}
        >
            <input
                type="text"
                name={name}
                value={inputValue}
                onChange={handleInputChange}
                onBlur={handleBlur}
                onFocus={handleFocus}
                disabled={disabled}
                className={`form-control ${
                    touched ? (error ? "is-invalid" : "is-valid") : ""
                }`}
                placeholder={placeholder}
                autoComplete="off"
            />
            <label>{label}</label>

            {showSuggestions && suggestions.length > 0 && (
                <div
                    ref={suggestionsRef}
                    className="suggestions-list position-absolute  border rounded"
                    style={{
                        top: "70%",
                        left: 0,
                        width: "100%",
                        maxHeight: "200px",
                        overflowY: "auto",
                        boxShadow: "0 -2px 5px rgba(0,0,0,0.1)",
                        zIndex: 1000,
                    }}
                >
                    {suggestions.map((suggestion, index) => (
                        <div
                            key={index}
                            onClick={() => handleSuggestionClick(suggestion)}
                            className="suggestion-item p-2 hover:bg-light"
                            style={{ cursor: "pointer" }}
                        >
                            {suggestion}
                        </div>
                    ))}
                </div>
            )}

            {touched && error && (
                <div className="invalid-feedback d-block">{error}</div>
            )}
        </div>
    );
};

const CustomPhoneInput = ({ value, onChange, inputClass, error, touched }) => {
    return (
        <div className="form-floating mt-24">
            <PhoneInput
                country={'ca'}
                value={value}
                onChange={onChange}
                inputClass={`form-control ${touched ? (error ? 'is-invalid' : 'is-valid') : ''}`}
                autoComplete="off"
                onlyCountries={['ca']} // Permet uniquement les numéros canadiens
            />
            <label className="mb-5" style={{ top: '-42px' }}>Numéro de téléphone</label>
            {error && <div className="invalid-feedback">{error}</div>}
        </div>
    );
};

const RegisterPage = () => {
    const {
        currentStep,
        isLoading,
        isSubmitting,
        formData,
        errors,
        touched,
        showPassword,
        showConfirmPassword,
        setShowPassword,
        setShowConfirmPassword,
        handleInputChange,
        handleNext,
        handlePrevious,
        handleSubmit,
        isStepValid,
        handlePhoneChange,
    } = useRegisterForm();

    // Fonction pour valider le pays par défaut (Canada)
    useEffect(() => {
        if (currentStep === 1 && formData.country === "Canada") {
            handleInputChange({
                target: {
                    name: "country",
                    value: "Canada",
                },
            });
        }
    }, [formData.country, currentStep]);

    // Validation de l'âge
    const validateAge = (dateOfBirthString) => {
        const today = new Date();
        const birthDate = new Date(dateOfBirthString);
        const age = today.getFullYear() - birthDate.getFullYear();
        const monthDifference = today.getMonth() - birthDate.getMonth();
        if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birthDate.getDate())) {
            return age - 1 >= 18;
        }
        return age >= 18;
    };

    const [isPageLoading, setIsPageLoading] = useState(true);

    useEffect(() => {
        const timer = setTimeout(() => {
            setIsPageLoading(false);
        }, 2000);

        return () => clearTimeout(timer);
    }, []);

    if (isPageLoading) {
        return <LoadingSpinner isVisible={true} loadingText="Chargement en cours..." />;
    }

    return (
        <>
            <div className="d-flex flex-column vh-100">
                <div className="flex-grow-1 overflow-y-scroll">

                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        maxWidth: '800px',
                        margin: '0 auto'
                    }}>
                        <div className="verify-number-main" style={{
                            marginTop: "10%",
                            borderRadius: '5%',
                            width: '100%',
                            maxWidth: '100%',
                        }}>
                            {isLoading && <Loading/>}
                            <ToastContainer/>

                            <div className="verify-number-top">
                                <div className="container">
                                    <Link to="/">
                                        <div className=" text-center" style={{paddingTop: "10px"}}>
                                            <img
                                                src="assets/images/logo/logo.png"
                                                alt="Logo NTR Finance"
                                                style={{width: "150px", marginBottom: "20px", marginTop: "20px", borderRadius: "50%"}}
                                            />
                                            <h1
                                                className="app-name"
                                                style={{
                                                    fontSize: "24px",
                                                    fontWeight: "bold",
                                                    margin: "10px 0",
                                                    color: "#fff",
                                                }}
                                            >
                                                NTR Finance
                                            </h1>

                                            <div className="header-title">
                                                <p>Inscription</p>
                                            </div>
                                        </div>
                                    </Link>
                                </div>
                            </div>

                            <div className="verify-number-bottom"
                                 style={{
                                     borderBottomLeftRadius: '5%',
                                     borderBottomRightRadius: '5%',
                                     minHeight: '10%'
                                 }}
                            >
                                <div className="verify-number-bottom-wrap">
                                    <form
                                        id="msform"
                                        onSubmit={(e) => handleSubmit(e, UserService)}
                                    >
                                        <div className="progress">
                                            <div
                                                className="progress-bar"
                                                role="progressbar"
                                                style={{
                                                    width: `${(currentStep + 1) * 33.33}%`,
                                                }}
                                            />
                                        </div>

                                        {/* Étape 1: Informations Personnelles */}
                                        <fieldset style={{display: currentStep === 0 ? 'block' : 'none'}}>
                                            <div className="personal-info">
                                                <div className="home-address-heading">
                                                    <h2>Informations Personnelles</h2>
                                                </div>
                                                <div className="multistep-form">
                                                    <div className="form-floating mt-24">
                                                        <input
                                                            type="text"
                                                            name="firstName"
                                                            value={formData.firstName}
                                                            onChange={handleInputChange}
                                                            className={`form-control ${touched.firstName ? (errors.firstName ? 'is-invalid' : 'is-valid') : ''}`}
                                                            placeholder="Prénom"
                                                            autoComplete="off"
                                                        />
                                                        <label>Prénom</label>
                                                        {errors.firstName &&
                                                            <div className="invalid-feedback">{errors.firstName}</div>}
                                                    </div>

                                                    <div className="form-floating mt-24">
                                                        <input
                                                            type="text"
                                                            name="lastName"
                                                            value={formData.lastName}
                                                            onChange={handleInputChange}
                                                            className={`form-control ${touched.lastName ? (errors.lastName ? 'is-invalid' : 'is-valid') : ''}`}
                                                            placeholder="Nom"
                                                            autoComplete="off"
                                                        />
                                                        <label>Nom</label>
                                                        {errors.lastName &&
                                                            <div className="invalid-feedback">{errors.lastName}</div>}
                                                    </div>

                                                    <div className="form-floating mt-24 mb-5">
                                                        <input
                                                            type="email"
                                                            name="email"
                                                            value={formData.email}
                                                            onChange={handleInputChange}
                                                            className={`form-control ${touched.email ? (errors.email ? 'is-invalid' : 'is-valid') : ''}`}
                                                            placeholder="Email"
                                                            autoComplete="off"
                                                        />
                                                        <label>Email</label>
                                                        {errors.email &&
                                                            <div className="invalid-feedback">{errors.email}</div>}
                                                    </div>

                                                    <CustomPhoneInput
                                                        value={formData.phoneNumber}
                                                        onChange={(value) => handlePhoneChange(value)}
                                                        inputClass={`form-control ${touched.phoneNumber ? (errors.phoneNumber ? 'is-invalid' : 'is-valid') : ''}`}
                                                        error={errors.phoneNumber}
                                                        touched={touched.phoneNumber}
                                                    />

                                                    <div className="form-floating mt-24">
                                                        <input
                                                            type="date"
                                                            name="dateOfBirthString"
                                                            value={formData.dateOfBirthString}
                                                            onChange={handleInputChange}
                                                            className={`form-control ${touched.dateOfBirthString ? (errors.dateOfBirthString ? 'is-invalid' : 'is-valid') : ''}`}
                                                            min="1900-01-01"
                                                            max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
                                                            autoComplete="off"
                                                        />
                                                        <label>Date de naissance</label>
                                                        {errors.dateOfBirthString &&
                                                            <div
                                                                className="invalid-feedback">{errors.dateOfBirthString}</div>}
                                                    </div>

                                                    {/*<div className="form-floating mt-24">
                                                        <select
                                                            name="gender"
                                                            value={formData.gender}
                                                            onChange={handleInputChange}
                                                            className="form-control"
                                                            autoComplete="off"
                                                        >
                                                            <option value="MALE">Homme</option>
                                                            <option value="FEMALE">Femme</option>
                                                        </select>
                                                        <label>Genre</label>
                                                    </div>*/}
                                                </div>
                                            </div>

                                            <div
                                                className="justify-content-start mt-4"
                                                style={{
                                                    justifyContent: "flex-start",
                                                }}
                                            >
                                                {isStepValid(0) && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-primary"
                                                        onClick={handleNext}
                                                    >
                                                        Suivant <i className="fa-solid fa-arrow-right-long"></i>
                                                    </button>
                                                )}

                                                {!isStepValid(0) && (
                                                    <p className="text-danger">
                                                        Vous devez correctement remplir tous les champs avant de
                                                        continuer.
                                                    </p>
                                                )}
                                            </div>
                                        </fieldset>

                                        {/* Étape 2: Informations d'Adresse */}
                                        <fieldset style={{display: currentStep === 1 ? 'block' : 'none'}}>
                                            <div className="address-info">
                                                <div className="home-address-heading mt-5">
                                                    <h2>Informations Additionnelles</h2>
                                                </div>
                                                <div className="multistep-form">
                                                    <div className="form-floating mt-24">
                                                        <input
                                                            type="text"
                                                            name="country"
                                                            value="Canada"
                                                            className="form-control"
                                                            placeholder="Pays"
                                                            disabled
                                                            autoComplete="off"
                                                        />
                                                        <label>Pays</label>
                                                    </div>


                                                    {/* Champ Province avec Autocomplete */}
                                                    <AutocompleteInput
                                                        name="province"
                                                        value={formData.province}
                                                        options={provincesCanada}
                                                        onChange={handleInputChange}
                                                        placeholder="Province"
                                                        label="Province"
                                                        error={errors.province}
                                                        touched={touched.province}
                                                    />

                                                    {/* Champ Ville avec Autocomplete - Autorise la saisie libre */}
                                                    <AutocompleteInput
                                                        name="city"
                                                        value={formData.city}
                                                        options={formData.province ? citiesCanada[formData.province] || [] : []}
                                                        onChange={handleInputChange}
                                                        placeholder="Ville"
                                                        label="Ville"
                                                        error={errors.city}
                                                        touched={touched.city}
                                                        disabled={!formData.province || errors.province}
                                                        allowFreeInput={true} // Autorise la saisie libre
                                                    />

                                                    <div className="form-floating mt-24">
                                                        <input
                                                            type="text"
                                                            name="streetAddress"
                                                            value={formData.streetAddress}
                                                            onChange={handleInputChange}
                                                            className={`form-control ${
                                                                touched.streetAddress
                                                                    ? (errors.streetAddress
                                                                        ? 'is-invalid'
                                                                        : 'is-valid')
                                                                    : ''
                                                            }`}
                                                            placeholder="Adresse"
                                                            autoComplete="off"
                                                        />
                                                        <label>Adresse</label>
                                                        {touched.streetAddress && errors.streetAddress && (
                                                            <div className="invalid-feedback d-block">
                                                                {errors.streetAddress}
                                                            </div>
                                                        )}
                                                    </div>


                                                    {/* Autres champs restent inchangés */}
                                                    {/*<div className="form-floating mt-24">
                                        <input
                                            type="text"
                                            name="neighborhood"
                                            value={formData.neighborhood}
                                            onChange={handleInputChange}
                                            className="form-control"
                                            placeholder="Quartier"
                                            autoComplete="off"
                                        />
                                        <label>Quartier</label>
                                    </div>*/}

                                                    {/*<div className="form-floating mt-24">
                                                        <input
                                                            type="text"
                                                            name="profession"
                                                            value={formData.profession}
                                                            onChange={handleInputChange}
                                                            className="form-control"
                                                            placeholder="Profession"
                                                            autoComplete="off"
                                                        />
                                                        <label>Profession</label>
                                                    </div>*/}


                                                    <div className="form-floating mt-24">
                                                        <input
                                                            type="text"
                                                            name="profession"
                                                            value={formData.profession}
                                                            onChange={handleInputChange}
                                                            className={`form-control ${
                                                                touched.profession ? (errors.profession ? 'is-invalid' : 'is-valid') : ''
                                                            }`}
                                                            placeholder="Profession"
                                                            autoComplete="off"
                                                        />
                                                        <label>Profession</label>
                                                        {touched.profession && errors.profession && (
                                                            <div className="invalid-feedback d-block">
                                                                {errors.profession}
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className="form-floating mt-24">
                                                        <input
                                                            type="text"
                                                            name="additionalInfos"
                                                            value={formData.additionalInfos}
                                                            onChange={handleInputChange}
                                                            className="form-control"
                                                            placeholder="Informations Additionnelles"
                                                            autoComplete="off"
                                                        />
                                                        <label>Informations Additionnelles</label>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="d-flex justify-content-between mt-4">
                                                <button type="button" className="btn btn-secondary"
                                                        onClick={handlePrevious}>
                                                    Précédent
                                                </button>
                                                {isStepValid(1) && (
                                                    <button type="button" className="btn btn-primary"
                                                            onClick={handleNext}>
                                                        Suivant <i className="fa-solid fa-arrow-right-long"></i>
                                                    </button>
                                                )}

                                            </div>

                                            {!isStepValid(1) && (
                                                <p className="text-danger mt-5">
                                                    Vous devez correctement remplir tous les champs obligatoir avant de
                                                    continuer.
                                                </p>
                                            )}
                                        </fieldset>

                                        {/* Étape 3: Informations de Connexion */}
                                        <fieldset style={{display: currentStep === 2 ? 'block' : 'none'}}>
                                            <div className="login-info">
                                                <div className="home-address-heading">
                                                    <h2>Informations de Connexion</h2>
                                                </div>
                                                <div className="multistep-form">
                                                    <div className="form-floating mt-24">
                                                        <input
                                                            type="text"
                                                            name="username"
                                                            value={formData.username}
                                                            onChange={handleInputChange}
                                                            className={`form-control ${touched.username ? (errors.username ? 'is-invalid' : 'is-valid') : ''}`}
                                                            placeholder="Nom d'utilisateur"
                                                            autoComplete="off"
                                                        />
                                                        <label>Nom d'utilisateur</label>
                                                        {errors.username &&
                                                            <div className="invalid-feedback">{errors.username}</div>}
                                                    </div>

                                                    <div className="form-floating mt-24 position-relative">
                                                        <input
                                                            type={showPassword ? "text" : "password"}
                                                            name="password"
                                                            value={formData.password}
                                                            onChange={handleInputChange}
                                                            className={`form-control ${touched.password ? (errors.password ? 'is-invalid' : 'is-valid') : ''}`}
                                                            placeholder="Mot de passe"
                                                            autoComplete="off"
                                                        />
                                                        <label>Mot de passe</label>
                                                        <button
                                                            type="button"
                                                            className="btn btn-link position-absolute end-0  translate-middle-y"
                                                            onClick={() => setShowPassword(!showPassword)}
                                                            style={{
                                                                zIndex: 5,
                                                                background: 'none',
                                                                border: 'none',
                                                                padding: '0.375rem 0.75rem',
                                                                top: '40%',
                                                                paddingRight: '30px',
                                                                fontSize: '35px'
                                                            }}
                                                        >
                                                            {showPassword ? <FaEyeSlash/> : <FaEye/>}
                                                        </button>
                                                        {errors.password &&
                                                            <div className="invalid-feedback">{errors.password}</div>}
                                                    </div>

                                                    <div className="form-floating mt-24 position-relative">
                                                        <input
                                                            type={showConfirmPassword ? "text" : "password"}
                                                            name="confirmPassword"
                                                            value={formData.confirmPassword}
                                                            onChange={handleInputChange}
                                                            className={`form-control ${touched.confirmPassword ? (errors.confirmPassword ? 'is-invalid' : 'is-valid') : ''}`}
                                                            placeholder="Confirmer le mot de passe"
                                                            autoComplete="off"
                                                        />
                                                        <label>Confirmer le mot de passe</label>
                                                        <button
                                                            type="button"
                                                            className="btn btn-link position-absolute end-0 top-50 translate-middle-y"
                                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                            style={{
                                                                zIndex: 5,
                                                                background: 'none',
                                                                border: 'none',
                                                                padding: '0.375rem 0.75rem',
                                                                top: '40%',
                                                                paddingRight: '30px',
                                                                fontSize: '35px'
                                                            }}
                                                        >
                                                            {showConfirmPassword ? <FaEyeSlash/> : <FaEye/>}
                                                        </button>
                                                        {errors.confirmPassword &&
                                                            <div
                                                                className="invalid-feedback">{errors.confirmPassword}</div>}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="d-flex justify-content-between mt-4">
                                                {!isSubmitting && (
                                                    <button type="button" className="btn btn-secondary"
                                                            onClick={handlePrevious}>
                                                        Précédent
                                                    </button>
                                                )}
                                                <button
                                                    type="submit"
                                                    className="btn btn-success"
                                                    disabled={!isStepValid(2) || isSubmitting}
                                                >
                                                    {isSubmitting ? (
                                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status"
                                                  aria-hidden="true"></span>
                                                            Inscription en cours...
                                                        </>
                                                    ) : (
                                                        "S'inscrire"
                                                    )}
                                                </button>
                                            </div>
                                        </fieldset>

                                        <div className="mt-32 text-center"
                                             style={{fontSize: '20px',fontWeight: 'bold',}}
                                        >
                                            <span>Vous avez déjà un compte ? </span>
                                            <Link to="/login" className="text-decoration-none">
                                                Connectez-vous ici.
                                            </Link>
                                        </div>

                                        <div className="mt-32 text-center" style={{ fontSize: '16px', fontWeight: 'bold', paddingBottom: '50px' }}>
                                            <Link to="/privacy_policy" className="text-decoration-none">
                                                Politique de confidentialité
                                            </Link>
                                        </div>

                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>

        </>
    );
};

export default RegisterPage;